<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-danger header-enhanced">
      <!-- Live Broadcast Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Broadcasts"
            type="gradient-danger"
            :sub-title="stats.totalBroadcasts ? stats.totalBroadcasts.toLocaleString() : 'Loading...'"
            icon="ni ni-world"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-danger mr-2">↗ {{ stats.broadcastGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Live Now"
            type="gradient-success"
            :sub-title="stats.liveBroadcasts ? stats.liveBroadcasts.toLocaleString() : 'Loading...'"
            icon="ni ni-button-play"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">● Broadcasting</span>
              <span class="text-nowrap">Currently active</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Scheduled"
            type="gradient-info"
            :sub-title="stats.scheduledBroadcasts ? stats.scheduledBroadcasts.toLocaleString() : 'Loading...'"
            icon="ni ni-time-alarm"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">⏰ Upcoming</span>
              <span class="text-nowrap">Ready to go live</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Total Viewers"
            type="gradient-purple"
            :sub-title="stats.totalViewers ? stats.totalViewers.toLocaleString() : 'Loading...'"
            icon="ni ni-single-02"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-purple mr-2">👁️ Watching</span>
              <span class="text-nowrap">All time</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-world mr-2"></i>Live Broadcast Management
          </h3>
          <p class="text-white-50 mb-0">Manage and monitor live streaming broadcasts</p>
        </b-col>
        <b-col class="text-right">
          <b-button
            variant="light"
            size="lg"
            class="shadow-sm update-list-btn"
            @click="updateBroadcastList">
            <i class="ni ni-refresh mr-2"></i>Update List
          </b-button>
        </b-col>
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <!-- Enhanced Filter Section -->
      <b-card class="mb-4 shadow-sm filter-card border-0">
        <b-row align-v="center">
          <b-col>
            <h5 class="mb-0 text-dark">
              <i class="ni ni-zoom-split-in mr-2 text-primary"></i>
              Filter & Search Broadcasts
            </h5>
            <small class="text-muted">Find specific broadcasts quickly</small>
          </b-col>
        </b-row>
        <div class="mt-3">
          <filter-bar @update-list="updateBroadcastList"></filter-bar>
        </div>
      </b-card>
      <vuetable data-path="broadcast"
                ref="vuetable"
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loading="showLoader"
                @vuetable:loaded="hideLoader"
                pagination-path="pagination"
                :api-url="apiUrl"
                :fields="fields"
                :http-options="httpOptions"
                :css="css.table"
                :per-page="10"
                :append-params="moreParams"
                :query-params="{
                  sort: 'sort',
                  page: 'page',
                  perPage: 'perPage'
                }"
                detail-row-component="live-broadcast-detail"
                track-by="live_broadcast_id"
                @vuetable:cell-clicked="onCellClicked">
        <template slot="link" slot-scope="props">
          <div class="link-container">
            <a :href="props.rowData.link"
               target="_blank"
               class="broadcast-link">
              <i class="ni ni-world mr-1"></i>
              {{ truncateLink(props.rowData.link) }}
            </a>
            <b-badge :variant="getStatusVariant(props.rowData.active)" class="ml-2">
              {{ props.rowData.active === 'yes' ? 'Live' : 'Offline' }}
            </b-badge>
          </div>
        </template>
        <template slot="action" slot-scope="props">
          <div class="action-buttons">
            <b-button
              size="sm"
              variant="danger"
              class="action-btn end-live-btn"
              @click="endLiveBroadcast(props.rowData)"
              v-show="props.rowData.active === 'yes'">
              <i class="ni ni-button-power mr-1"></i>End Live
            </b-button>
            <b-button
              size="sm"
              variant="success"
              class="action-btn set-live-btn"
              @click="setLiveBroadcast(props.rowData)"
              v-show="!isLiveSet">
              <i class="ni ni-button-play mr-1"></i>Set Live
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              class="action-btn edit-btn"
              @click="editLiveBroadcast(props.rowData)"
              v-show="!isLiveSet">
              <i class="ni ni-settings mr-1"></i>Edit
            </b-button>
          </div>
        </template>
      </vuetable>

      <!-- Enhanced Pagination -->
      <b-card class="mt-4 shadow-sm pagination-card">
        <b-row align-v="center">
          <b-col md="6">
            <div class="pagination-info">
              <vuetable-pagination-info ref="paginationInfo" class="mb-0 text-muted"></vuetable-pagination-info>
            </div>
          </b-col>
          <b-col md="6" class="text-right">
            <div class="pagination-controls">
              <vuetable-pagination
                ref="pagination"
                @vuetable-pagination:change-page="onChangePage"
                :css="css.pagination">
              </vuetable-pagination>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </div>
    <!-- Dialog -->
    <v-dialog/>
    <modals-container v-on:modalClose="onModalClose"/>
    <!-- Loading -->
    <loading :active.sync="loading"
             :is-full-page="true"></loading>
    <!-- <loading :active.sync="isLoading"
             :is-full-page="true"></loading> -->
  </div>
</template>

<script>
  /* eslint-disable camelcase */
  import Vue from 'vue';
  import TableStyle from '../table-bootstrap-css.js';
  import 'vue-loading-overlay/dist/vue-loading.css';
  import moment from 'moment-timezone';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';

  const Loading = () => import('vue-loading-overlay');
  const Vuetable = () => import('vuetable-2/src/components/Vuetable');
  const VuetablePagination = () =>
  import('vuetable-2/src/components/VuetablePagination');
  const VuetablePaginationInfo = () =>
  import('vuetable-2/src/components/VuetablePaginationInfo');
  const LiveBroadcastDetail = () => import('./LiveBroadcastDetail.vue');
  const FilterBar = () => import('./FilterBar.vue');
  const EditLiveBroadcast = () => import('./EditLiveBroadcast.vue');

  Vue.component('live-broadcast-detail', LiveBroadcastDetail);

  export default {
    name : 'LiveBroadcast',
    data() {
      return {
        apiUrl      : process.env.VUE_APP_ROOT_API + 'api/live_broadcast/list',
        moreParams  : {},
        css         : TableStyle,
        httpOptions : {
          headers : {
            'Authorization' : `Bearer ${this.$store.state.auth.accessToken}`,
          },
        },
        stats: {
          totalBroadcasts: null,
          liveBroadcasts: null,
          scheduledBroadcasts: null,
          totalViewers: null,
          broadcastGrowth: 0,
        },
        fields : [
          {
            name      : 'live_broadcast_id',
            title     : 'Broadcast ID',
            sortField : 'live_broadcast_id',
            direction : 'desc',
          },
          {
            name      : 'name',
            title     : 'Name',
            sortField : 'name',
            direction : 'desc',
          },
          {
            name      : '__slot:link',
            title     : 'Link',
            sortField : 'link',
            direction : 'desc',
          },
          {
            name      : 'start_time',
            title     : 'Start Time',
            sortField : 'start_time',
          },
          {
            name      : 'created_time',
            title     : 'Date Created',
            sortField : 'created_time',
            direction : 'desc',
          },
          {
            name       : '__slot:action',
            title      : 'Actions',
            titleClass : 'text-center',
            dataClass  : 'text-center',
          },
        ],
        isLiveSet : false,
        loading   : false,
        userTz    : this.$store.getters['user/user'].timezone,
      }
    },
    components : {
      // eslint-disable-next-line vue/no-unused-components
      EditLiveBroadcast,
      FilterBar,
      Loading,
      Vuetable,
      VuetablePagination,
      VuetablePaginationInfo,
      baseHeader,
      StatsCard
    },
    methods : {

      /**
       * Transform data
       * @param data
       * @return {object}
       */
      transform(data) {
        const transformed = {};

        transformed.pagination = {
          total         : data.total,
          per_page      : data.perPage,
          current_page  : data.currentPage,
          last_page     : data.lastPage,
          next_page_url : data.nextPageUrl,
          prev_page_url : data.prevPageUrl,
          from          : data.from,
          to            : data.to,
        };

        transformed.broadcast = [];

        for (let i= 0; i < data.data.length; i++) {
          // Date conversion from UTC to User Time Zone
          const created = moment.utc(data.data[i].created_time);
          const createdConv = moment.tz(created, this.userTz)
            .format('MMM DD, YYYY, hh:mm:ss A z');
          const start = moment.utc(data.data[i].start_time);
          const startConv = (data.data[i].start_time !== '') ?
            moment.tz(start, this.userTz).format('hh:mm A z') : 'Unset';

          transformed.broadcast.push({
            live_broadcast_id : data.data[i].live_broadcast_id,
            uri               : data.data[i].uri,
            name              : data.data[i].name,
            link              : data.data[i].link,
            html_embed        : data.data[i].html_embed,
            active            : data.data[i].active,
            start_time        : startConv,
            created_time      : createdConv,
          });

          if (data.data[i].active === 'yes')
            this.isLiveSet = true;
        }

        return transformed;
      },

      /**
       * Set Pagination Info
       * @param paginationData
       */
      onPaginationData(paginationData) {
        this.$refs.pagination.setPaginationData(paginationData);
        this.$refs.paginationInfo.setPaginationData(paginationData);
      },

      /**
       * Change page
       * @param page
       */
      onChangePage(page) {
        this.$refs.vuetable.changePage(page);
      },

      /**
       * On Live Broadcast Cell Clicked
       * @param data
       */
      onCellClicked(data) {
        this.$refs.vuetable.toggleDetailRow(data.live_broadcast_id);
      },

      /**
       * On Filter Set
       * @param filterText
       */
      onFilterSet(filterText) {
        this.moreParams = {
          'filter' : filterText,
        };
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },

      /**
       * On Filter Reset
       */
      onFilterReset() {
        this.moreParams = {};
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },

      /**
       * Update Broadcast List
       */
      updateBroadcastList() {
        this.loading = true;
        this.$http.get('api/live_broadcast/list/update')
          .then(() => {
            this.loading = false;
            Vue.nextTick(() => this.$refs.vuetable.reload());
            this.$notify({
              group : 'notif',
              type  : 'success',
              title : 'Success',
              text  : 'Updated Live Broadcast List!',
            });
          })
          .catch((error) => {
            this.loading = false;
            if (error.response.status === 400) {
              this.$notify({
                group : 'notif',
                type  : 'success',
                title : 'Success',
                text  : 'Live Broadcast List is up to date!',
              });
            } else {
              this.$notify({
                group : 'notif',
                type  : 'error',
                title : 'Failed',
                text  : 'Oops! Something went wrong!',
              });
            }
          }
          );
      },

      /**
       * Set Live Broadcast
       * @param broadcast
       */
      setLiveBroadcast(broadcast) {
        this.$modal.show('dialog', {
          title : 'Set Live',
          text  : 'Are you sure you want to set ' + broadcast.name +
            ' as live?',
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.$http.post('api/live_broadcast/set', {
                liveBroadcastId : broadcast.live_broadcast_id,
                action          : 'set',
              }).then(() => {
                this.isLiveSet = true;
                this.$modal.hide('dialog');
                Vue.nextTick(() => this.$refs.vuetable.reload());
                this.$notify({
                  group : 'notif',
                  type  : 'success',
                  title : 'Success!',
                  text  : 'Set ' + broadcast.name + ' as live!',
                });
              }).catch(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'error',
                  title : 'Failed!',
                  text  : 'Oops! Something went wrong!',
                });
              });
            },
          }, {
            title : 'No',
          } ],
        });
      },

      /**
       * End Live Broadcast
       * @param broadcast
       */
      endLiveBroadcast(broadcast) {
        this.$modal.show('dialog', {
          title   : 'End Live',
          text    : 'Are you sure you want to end ' + broadcast.name + '?',
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.$http.post('api/live_broadcast/unset', {
                liveBroadcastId : broadcast.live_broadcast_id,
                action          : 'unset',
              }).then(() => {
                this.isLiveSet = false;
                this.$modal.hide('dialog');
                Vue.nextTick(() => this.$refs.vuetable.reload());
                this.$notify({
                  group : 'notif',
                  type  : 'success',
                  title : 'Success!',
                  text  : 'Ended ' + broadcast.name + ' live broadcast!',
                });
              }).catch(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'error',
                  title : 'Failed!',
                  text  : 'Oops! Something went wrong!',
                });
              });
            },
          }, {
            title : 'No',
          } ],
        });
      },

      /**
       * Edit Live Broadcast
       * @param broadcast
       */
      editLiveBroadcast(broadcast) {
        this.$modal.show(
          EditLiveBroadcast,
          { data : broadcast },
          { height : 'auto', adaptive : true });
      },

      /**
       * Close modal
       */
      onModalClose() {
        this.$refs.vuetable.reload();
      },

      /**
       * Show Loader
       */
      showLoader() {
        this.loading = true;
      },

      /**
       * hide Loader
       */
      hideLoader() {
        this.loading = false;
      },

      /**
       * Initialize Broadcast Statistics
       */
      initializeStats() {
        // Simulated broadcast statistics - replace with actual API calls
        this.stats.totalBroadcasts = 23;
        this.stats.liveBroadcasts = 1;
        this.stats.scheduledBroadcasts = 5;
        this.stats.totalViewers = 8750;
        this.stats.broadcastGrowth = 18.7;
      },

      /**
       * Truncate Link for Display
       */
      truncateLink(link) {
        if (!link) return '';
        if (link.length <= 40) return link;
        return link.substring(0, 37) + '...';
      },

      /**
       * Get Status Badge Variant
       */
      getStatusVariant(status) {
        return status === 'yes' ? 'success' : 'secondary';
      },
    },
    mounted() {
      this.initializeStats();
      // Update live_broadcast db table
      this.updateBroadcastList();

      this.$events.$on('filter-set', eventData => this.onFilterSet(eventData));
      this.$events.$on('filter-reset', () => this.onFilterReset());
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/components/admin/broadcast/live-broadcast";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.9) 0%, rgba(176, 42, 55, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(220, 53, 69, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(176, 42, 55, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Filter Card */
.filter-card {
  border-radius: 12px !important;
  border: none !important;
  transition: all 0.3s ease;
}

.filter-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.update-list-btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

.update-list-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
}

/* Enhanced Table Styles */
table {
  z-index: 99;
  position: relative;
  background: white;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  animation: fadeInUp 0.8s ease-out;
}

.vuetable th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  color: #495057 !important;
  padding: 12px 8px !important;
}

.vuetable td {
  padding: 12px 8px !important;
  border-bottom: 1px solid #f1f3f4 !important;
  vertical-align: middle !important;
}

.vuetable tbody tr:hover {
  background-color: rgba(220, 53, 69, 0.05) !important;
  transform: scale(1.01);
  transition: all 0.2s ease;
}

/* Enhanced Link Container */
.link-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.broadcast-link {
  color: #5e72e4;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.broadcast-link:hover {
  color: #324cdd;
  text-decoration: underline;
}

/* Enhanced Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.end-live-btn {
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.end-live-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

.set-live-btn {
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.set-live-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

.edit-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

/* Enhanced Pagination */
.pagination-card {
  border-radius: 12px !important;
  border: none !important;
  background: rgba(248, 249, 250, 0.8);
}

.pagination-info {
  padding: 16px 20px;
  font-weight: 500;
  color: #6c757d;
}

.pagination-controls {
  padding: 16px 20px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-card {
  animation: fadeInUp 0.8s ease-out;
}

/* Responsive Design */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .filter-card:hover {
    transform: none;
  }

  .update-list-btn:hover {
    transform: none;
  }

  .action-btn:hover {
    transform: none;
  }

  .vuetable tbody tr:hover {
    transform: none;
  }

  .broadcast-link {
    max-width: 150px;
  }

  .action-buttons {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-btn {
    width: 100%;
    margin-bottom: 0.25rem;
  }
}

/* Legacy styles */
.baseHeader, .header {
  margin-top: -80px;
}
</style>
